import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  CurrencyCircleDollar,
  ChartLineUp,
  ChartLineDown,
  Calendar,
  TrendUp,
  TrendDown,
  Pencil,
  Trash,
  Receipt,
  Users,
  Carrot,
  Wallet,
  CreditCard,
  Truck,
  Package,
} from '@phosphor-icons/react';
import { TrendingUp, TrendingDown, DollarSign, PieChart, Beef } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function ProfitLoss() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [salesData, setSalesData] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMonthlyDialogOpen, setIsMonthlyDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Tarih filtreleri
  const [filterMode, setFilterMode] = useState('month'); // month veya range
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [viewMode, setViewMode] = useState('monthly'); // daily, weekly, monthly
  const [activeTab, setActiveTab] = useState('summary'); // summary, daily, weekly
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    cash_revenue: '',
    card_revenue: '',
    online_revenue: '',
    customer_count: '',
    notes: '',
  });

  // Otomatik toplam hesaplama
  const calculatedTotal = (parseFloat(formData.cash_revenue) || 0) + 
                          (parseFloat(formData.card_revenue) || 0) + 
                          (parseFloat(formData.online_revenue) || 0);

  // Aylık ciro formu
  const [monthlyFormData, setMonthlyFormData] = useState({
    month: '',
    total_revenue: '',
    working_days: '',
    notes: '',
  });

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, [selectedMonth, startDate, endDate, filterMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let salesUrl = '/daily-sales';
      let reportUrl = '/reports/profit-loss?period=daily';
      
      if (filterMode === 'range' && startDate && endDate) {
        salesUrl += `?start_date=${startDate}&end_date=${endDate}`;
        reportUrl += `&start_date=${startDate}&end_date=${endDate}`;
      } else {
        salesUrl += `?month=${selectedMonth}`;
        reportUrl += `&month=${selectedMonth}`;
      }
      
      const [salesRes, reportRes] = await Promise.all([
        api.get(salesUrl),
        api.get(reportUrl)
      ]);
      setSalesData(salesRes.data);
      setReport(reportRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      cash_revenue: '',
      card_revenue: '',
      online_revenue: '',
      customer_count: '',
      notes: '',
    });
  };

  const handleSubmit = async () => {
    if (calculatedTotal <= 0) {
      toast.error('En az bir ödeme türü girilmeli');
      return;
    }

    try {
      const payload = {
        date: formData.date,
        total_revenue: calculatedTotal,
        cash_revenue: parseFloat(formData.cash_revenue) || 0,
        card_revenue: parseFloat(formData.card_revenue) || 0,
        online_revenue: parseFloat(formData.online_revenue) || 0,
        customer_count: parseInt(formData.customer_count) || 0,
        notes: formData.notes,
      };

      if (editingEntry) {
        await api.put(`/daily-sales/${editingEntry.id}`, payload);
        toast.success('Ciro güncellendi');
      } else {
        await api.post('/daily-sales', payload);
        toast.success('Ciro kaydedildi');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      cash_revenue: (entry.cash_revenue || 0).toString(),
      card_revenue: (entry.card_revenue || 0).toString(),
      online_revenue: (entry.online_revenue || 0).toString(),
      customer_count: (entry.customer_count || 0).toString(),
      notes: entry.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu ciro kaydını silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/daily-sales/${id}`);
      toast.success('Kayıt silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // Aylık ciro ekleme
  const handleMonthlySubmit = async () => {
    if (!monthlyFormData.month || !monthlyFormData.total_revenue) {
      toast.error('Ay ve toplam ciro gerekli');
      return;
    }

    try {
      const payload = {
        month: monthlyFormData.month,
        total_revenue: parseFloat(monthlyFormData.total_revenue),
        working_days: monthlyFormData.working_days ? parseInt(monthlyFormData.working_days) : null,
        notes: monthlyFormData.notes || null,
      };

      const res = await api.post('/daily-sales/monthly', payload);
      toast.success(res.data.message);
      setIsMonthlyDialogOpen(false);
      setMonthlyFormData({ month: '', total_revenue: '', working_days: '', notes: '' });
      
      // Eklenen ayı seç ve verileri yenile
      setSelectedMonth(monthlyFormData.month);
      setFilterMode('month');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  // Aylık kayıtları silme
  const handleDeleteMonthly = async () => {
    if (!confirm(`${selectedMonth} ayındaki TÜM ciro kayıtlarını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    
    try {
      const res = await api.delete(`/daily-sales/monthly/${selectedMonth}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const formatCurrency = (value) => {
    return `₺${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', { 
      day: 'numeric',
      month: 'short'
    });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', { 
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  };

  const getProfitColor = (profit) => {
    if (profit > 0) return 'text-green-600';
    if (profit < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  const getProfitBg = (profit) => {
    if (profit > 0) return 'bg-green-50 border-green-200';
    if (profit < 0) return 'bg-red-50 border-red-200';
    return 'bg-slate-50 border-slate-200';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="profit-loss-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <ChartLineUp className="w-8 h-8 text-emerald-600" />
              {t('profitLoss.title')}
            </h1>
            <p className="text-muted-foreground">{t('profitLoss.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Filtre Modu Seçimi */}
            <div className="flex items-center gap-2 border rounded-lg p-1">
              <Button
                variant={filterMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilterMode('month')}
              >
                {t('common.month')}
              </Button>
              <Button
                variant={filterMode === 'range' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilterMode('range')}
              >
                {t('common.dateRange')}
              </Button>
            </div>
            
            {/* Ay veya Tarih Aralığı */}
            {filterMode === 'month' ? (
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-40"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36"
                  placeholder={t('common.from')}
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36"
                  placeholder="Bitiş"
                />
              </div>
            )}
            
            {isManager && (
              <>
                {/* Aylık Ciro Ekle */}
                <Dialog open={isMonthlyDialogOpen} onOpenChange={setIsMonthlyDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-emerald-300 text-emerald-600 hover:bg-emerald-50">
                      <Calendar className="w-4 h-4 mr-2" />
                      Aylık Ciro
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Aylık Ciro Girişi</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                        Girdiğiniz toplam ciro, çalışılan gün sayısına bölünerek her güne eşit dağıtılacaktır.
                      </div>
                      
                      <div>
                        <Label>Ay *</Label>
                        <Input
                          type="month"
                          value={monthlyFormData.month}
                          onChange={(e) => setMonthlyFormData({ ...monthlyFormData, month: e.target.value })}
                        />
                      </div>
                      
                      <div>
                        <Label>Toplam Aylık Ciro (₺) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={monthlyFormData.total_revenue}
                          onChange={(e) => setMonthlyFormData({ ...monthlyFormData, total_revenue: e.target.value })}
                          placeholder="1350000"
                          className="text-lg font-bold"
                        />
                      </div>
                      
                      <div>
                        <Label>Çalışılan Gün Sayısı (opsiyonel)</Label>
                        <Input
                          type="number"
                          value={monthlyFormData.working_days}
                          onChange={(e) => setMonthlyFormData({ ...monthlyFormData, working_days: e.target.value })}
                          placeholder="30 (boş bırakılırsa ayın tüm günleri)"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Boş bırakılırsa ayın tüm günlerine bölünür
                        </p>
                      </div>
                      
                      {monthlyFormData.total_revenue && monthlyFormData.month && (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <p className="text-sm text-emerald-700">
                            Günlük Ortalama: <strong>₺{(parseFloat(monthlyFormData.total_revenue) / (parseInt(monthlyFormData.working_days) || 30)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
                          </p>
                        </div>
                      )}
                      
                      <Button onClick={handleMonthlySubmit} className="w-full bg-emerald-600 hover:bg-emerald-700">
                        Aylık Ciro Ekle ve Günlere Böl
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Günlük Ciro Ekle */}
                <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-sales-btn" className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Ciro Ekle
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>{editingEntry ? 'Ciro Düzenle' : 'Günlük Ciro Girişi'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Tarih *</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Nakit *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.cash_revenue}
                          onChange={(e) => setFormData({ ...formData, cash_revenue: e.target.value })}
                          placeholder="18000"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Kart *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.card_revenue}
                          onChange={(e) => setFormData({ ...formData, card_revenue: e.target.value })}
                          placeholder="25000"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Online *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.online_revenue}
                          onChange={(e) => setFormData({ ...formData, online_revenue: e.target.value })}
                          placeholder="2000"
                        />
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <Label className="text-blue-700 text-sm">Toplam Ciro (Otomatik Hesaplanan)</Label>
                      <div className="text-2xl font-bold text-blue-700">
                        ₺{calculatedTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </div>
                      <p className="text-xs text-blue-600 mt-1">Nakit + Kart + Online = Toplam</p>
                    </div>
                    
                    <div>
                      <Label>Müşteri Sayısı</Label>
                      <Input
                        type="number"
                        value={formData.customer_count}
                        onChange={(e) => setFormData({ ...formData, customer_count: e.target.value })}
                        placeholder="150"
                      />
                    </div>
                    
                    <div>
                      <Label>Not</Label>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Özel gün, etkinlik vb."
                      />
                    </div>
                    
                    <Button onClick={handleSubmit} className="w-full bg-emerald-600 hover:bg-emerald-700">
                      {editingEntry ? 'Güncelle' : 'Kaydet'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Özet Kartları */}
        {report && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Toplam Ciro */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 font-medium uppercase">{t('profitLoss.totalRevenue')}</p>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(report.summary.total_revenue)}</p>
                    <p className="text-xs text-blue-600">{report.revenue_days} {language === 'tr' ? 'gün satış' : 'days sales'}</p>
                  </div>
                  <div className="p-3 bg-blue-200 rounded-full">
                    <CurrencyCircleDollar className="w-6 h-6 text-blue-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Toplam Gider */}
            <Card className="border-2 border-orange-200 bg-orange-50">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-700 font-medium uppercase">{t('profitLoss.totalCost')}</p>
                    <p className="text-2xl font-bold text-orange-700">{formatCurrency(report.summary.total_cost)}</p>
                    <p className="text-xs text-orange-600">{report.period.total_days} {language === 'tr' ? 'gün' : 'days'}</p>
                  </div>
                  <div className="p-3 bg-orange-200 rounded-full">
                    <Wallet className="w-6 h-6 text-orange-700" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Net Kar/Zarar */}
            <Card className={`border-2 ${getProfitBg(report.summary.net_profit)}`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-medium uppercase ${getProfitColor(report.summary.net_profit)}`}>
                      {report.summary.net_profit >= 0 ? t('profitLoss.netProfit') : (language === 'tr' ? 'Net Zarar' : 'Net Loss')}
                    </p>
                    <p className={`text-2xl font-bold ${getProfitColor(report.summary.net_profit)}`}>
                      {formatCurrency(Math.abs(report.summary.net_profit))}
                    </p>
                    <p className={`text-xs ${getProfitColor(report.summary.net_profit)}`}>
                      %{report.summary.profit_margin.toFixed(1)} {language === 'tr' ? 'marj' : 'margin'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${report.summary.net_profit >= 0 ? 'bg-green-200' : 'bg-red-200'}`}>
                    {report.summary.net_profit >= 0 ? (
                      <TrendUp className="w-6 h-6 text-green-700" />
                    ) : (
                      <TrendDown className="w-6 h-6 text-red-700" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Günlük Ortalama */}
            <Card className="border-2 border-purple-200 bg-purple-50">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-700 font-medium uppercase">Günlük Ort. Kar</p>
                    <p className={`text-2xl font-bold ${getProfitColor(report.summary.average_daily_profit)}`}>
                      {formatCurrency(report.summary.average_daily_profit)}
                    </p>
                    <p className="text-xs text-purple-600">
                      Ciro: {formatCurrency(report.summary.average_daily_revenue)}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-200 rounded-full">
                    <Calendar className="w-6 h-6 text-purple-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Butonları */}
        <div className="flex gap-2 border-b">
          <Button 
            variant={activeTab === 'summary' ? 'default' : 'ghost'} 
            onClick={() => setActiveTab('summary')}
            className="rounded-b-none"
          >
            <PieChart className="w-4 h-4 mr-2" />
            Gider Dağılımı
          </Button>
          <Button 
            variant={activeTab === 'daily' ? 'default' : 'ghost'} 
            onClick={() => setActiveTab('daily')}
            className="rounded-b-none"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Günlük Detay
          </Button>
          <Button 
            variant={activeTab === 'weekly' ? 'default' : 'ghost'} 
            onClick={() => setActiveTab('weekly')}
            className="rounded-b-none"
          >
            <ChartLineUp className="w-4 h-4 mr-2" />
            Haftalık Özet
          </Button>
          <Button 
            variant={activeTab === 'entries' ? 'default' : 'ghost'} 
            onClick={() => setActiveTab('entries')}
            className="rounded-b-none"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Ciro Girişleri
          </Button>
        </div>

        {/* Gider Dağılımı Tab */}
        {activeTab === 'summary' && report && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gider Kategorileri */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-orange-600" />
                  Gider Dağılımı
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(report.expense_by_category || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, amount], idx) => {
                      const percentage = report.summary.total_cost > 0 
                        ? (amount / report.summary.total_cost * 100) 
                        : 0;
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{category}</span>
                            <span className="text-muted-foreground">
                              {formatCurrency(amount)} (%{percentage.toFixed(1)})
                            </span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-orange-500 rounded-full transition-all"
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* Maliyet Özeti */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-blue-600" />
                  Maliyet Özeti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-8 h-8 text-orange-600" />
                      <div>
                        <p className="font-medium">Genel Giderler</p>
                        <p className="text-sm text-muted-foreground">Kira, fatura, malzeme vb.</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-orange-700">
                      {formatCurrency(report.cost_breakdown.expenses)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <Carrot className="w-8 h-8 text-green-600" />
                      <div>
                        <p className="font-medium">Sebze/Manav</p>
                        <p className="text-sm text-muted-foreground">Taze ürün alımları</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-green-700">
                      {formatCurrency(report.cost_breakdown.vegetables)}
                    </span>
                  </div>
                  
                  {/* Et/Tavuk */}
                  {report.cost_breakdown.meat > 0 && (
                    <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-3">
                        <Beef className="w-8 h-8 text-red-600" />
                        <div>
                          <p className="font-medium">Et/Tavuk</p>
                          <p className="text-sm text-muted-foreground">Et ürün alımları</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-red-700">
                        {formatCurrency(report.cost_breakdown.meat)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="font-medium">Personel Maaşları</p>
                        <p className="text-sm text-muted-foreground">{report.period.total_days} günlük maliyet</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-blue-700">
                      {formatCurrency(report.cost_breakdown.staff)}
                    </span>
                  </div>
                  
                  {/* Vadesi Gelen Çekler */}
                  {report.cost_breakdown.checks > 0 && (
                    <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-8 h-8 text-purple-600" />
                        <div>
                          <p className="font-medium">Vadesi Gelen Çekler</p>
                          <p className="text-sm text-muted-foreground">{report.checks_count || 0} adet verilen çek</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-purple-700">
                        {formatCurrency(report.cost_breakdown.checks)}
                      </span>
                    </div>
                  )}
                  
                  {/* Genel Ürünler */}
                  {report.cost_breakdown.general > 0 && (
                    <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                      <div className="flex items-center gap-3">
                        <Package className="w-8 h-8 text-indigo-600" />
                        <div>
                          <p className="font-medium">Genel Ürünler</p>
                          <p className="text-sm text-muted-foreground">Temizlik, içecek, ambalaj vb.</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-indigo-700">
                        {formatCurrency(report.cost_breakdown.general)}
                      </span>
                    </div>
                  )}
                  
                  {/* Sevkiyat Maliyeti - her zaman göster */}
                  <div className="flex items-center justify-between p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                    <div className="flex items-center gap-3">
                      <Truck className="w-8 h-8 text-cyan-600" />
                      <div>
                        <p className="font-medium">Sevkiyat Maliyeti</p>
                        <p className="text-sm text-muted-foreground">Depo → Mağaza transfer</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-cyan-700">
                      {formatCurrency(report.cost_breakdown.transfer || 0)}
                    </span>
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold">Toplam Maliyet</span>
                      <span className="text-2xl font-bold text-orange-700">
                        {formatCurrency(report.summary.total_cost)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Günlük Detay Tab */}
        {activeTab === 'daily' && report && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Günlük Kar/Zarar</CardTitle>
            </CardHeader>
            <CardContent>
              {report.daily_breakdown?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Bu dönemde veri yok</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead className="text-right">Ciro</TableHead>
                        <TableHead className="text-right">Gider</TableHead>
                        <TableHead className="text-right">Sebze</TableHead>
                        <TableHead className="text-right">Et/Tavuk</TableHead>
                        <TableHead className="text-right">Personel</TableHead>
                        <TableHead className="text-right">Toplam Maliyet</TableHead>
                        <TableHead className="text-right">Kar/Zarar</TableHead>
                        <TableHead className="text-right">Marj</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.daily_breakdown.map((day, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{formatFullDate(day.date)}</TableCell>
                          <TableCell className="text-right text-blue-600 font-semibold">
                            {day.revenue > 0 ? formatCurrency(day.revenue) : '-'}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(day.expenses)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(day.vegetables)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(day.meat)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(day.staff_cost)}</TableCell>
                          <TableCell className="text-right text-orange-600">{formatCurrency(day.total_cost)}</TableCell>
                          <TableCell className={`text-right font-bold ${getProfitColor(day.profit)}`}>
                            {day.profit >= 0 ? '+' : ''}{formatCurrency(day.profit)}
                          </TableCell>
                          <TableCell className={`text-right ${getProfitColor(day.profit)}`}>
                            %{day.profit_margin}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Haftalık Özet Tab */}
        {activeTab === 'weekly' && report && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Haftalık Kar/Zarar Özeti</CardTitle>
            </CardHeader>
            <CardContent>
              {report.weekly_breakdown?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Bu dönemde veri yok</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {report.weekly_breakdown.map((week, idx) => (
                    <Card key={idx} className={`border-2 ${getProfitBg(week.profit)}`}>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-bold text-lg">{week.week}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(week.start_date)} - {formatDate(week.end_date)} ({week.days} gün)
                            </p>
                          </div>
                          {week.profit >= 0 ? (
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Ciro:</span>
                            <span className="font-semibold text-blue-600">{formatCurrency(week.revenue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Maliyet:</span>
                            <span className="font-semibold text-orange-600">{formatCurrency(week.cost)}</span>
                          </div>
                          <div className="border-t pt-2 flex justify-between">
                            <span className="font-medium">Kar/Zarar:</span>
                            <span className={`font-bold ${getProfitColor(week.profit)}`}>
                              {week.profit >= 0 ? '+' : ''}{formatCurrency(week.profit)}
                            </span>
                          </div>
                          <div className="text-center">
                            <Badge className={week.profit >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              %{week.profit_margin} marj
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Ciro Girişleri Tab */}
        {activeTab === 'entries' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">
                {filterMode === 'month' ? getMonthName(selectedMonth) : `${formatDate(startDate)} - ${formatDate(endDate)}`} Ciro Girişleri
              </CardTitle>
              {isAdmin && salesData.length > 0 && filterMode === 'month' && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={handleDeleteMonthly}
                >
                  <Trash className="w-4 h-4 mr-2" />
                  Ayı Sil
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : salesData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CurrencyCircleDollar size={48} className="mx-auto mb-3 opacity-50" />
                  <p>Bu ayda ciro girişi yok</p>
                  <p className="text-sm mt-2">Yukarıdaki "Ciro Ekle" butonunu kullanarak günlük ciro ekleyin</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead className="text-right">Toplam Ciro</TableHead>
                        <TableHead className="text-right">Nakit</TableHead>
                        <TableHead className="text-right">Kart</TableHead>
                        <TableHead className="text-right">Online</TableHead>
                        <TableHead className="text-right">Müşteri</TableHead>
                        <TableHead>Not</TableHead>
                        <TableHead className="text-center">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salesData.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">{formatFullDate(entry.date)}</TableCell>
                          <TableCell className="text-right font-bold text-blue-600">
                            {formatCurrency(entry.total_revenue)}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.cash_revenue)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.card_revenue)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.online_revenue)}</TableCell>
                          <TableCell className="text-right">{entry.customer_count || '-'}</TableCell>
                          <TableCell className="text-muted-foreground">{entry.notes || '-'}</TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              {isManager && (
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDelete(entry.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
